import { useEffect, useState } from 'react';
import axios from 'axios';
import toastr from 'toastr';
import { Building2, Pencil, ChevronRight, ChevronLeft, RefreshCw } from 'lucide-react';
import JgAdvisorList from './JgAdvisorList';
import JgAdvisorCodeEditor from './JgAdvisorCodeEditor';

const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

// Persistent side drawer listing JG submissions whose advisor/distro accounts were auto-created
// in KTM but still need a Chamber Code. Each company has an "Update Chamber Code" button that
// opens a popup; saving writes the code onto the matched distro account's A1 Number, marks the
// submission reviewed, and removes it from the list.
// Rendered outside the Advisors page tab switch so it stays open on every view.
const JgIncomingDrawer = () => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/Advisors/jg-pending');
            setItems(res.data.pending || []);
        } catch (error) {
            toastr.error('Failed to load JG incoming advisors', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const onSaved = (id) => {
        setItems((prev) => prev.filter((x) => x.id !== id));
        setEditingId(null);
    };

    return (
        <div className={`jg-drawer${open ? ' open' : ''}`}>
            <button className="jg-drawer-handle" onClick={() => setOpen(!open)} title="JG Incoming Advisors">
                {open ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                <span className="jg-drawer-handle-label">JG Incoming</span>
                {items.length > 0 && <span className="jg-drawer-badge">{items.length}</span>}
            </button>
            <div className="jg-drawer-panel">
                <div className="jg-drawer-header">
                    <div>
                        <h5>JG Incoming Advisors</h5>
                        <p>Enter each Chamber Code to review</p>
                    </div>
                    <button className="jg-refresh" onClick={fetchData} title="Refresh">
                        <RefreshCw size={16} />
                    </button>
                </div>
                <div className="jg-drawer-body">
                    {loading ? (
                        <div className="jg-empty">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="jg-empty">Nothing pending review.</div>
                    ) : (
                        items.map((item) => (
                            <div className="jg-card" key={item.id}>
                                <div className="jg-card-top">
                                    <span className="jg-company">
                                        <Building2 size={14} /> {item.companyName || 'Unknown company'}
                                    </span>
                                </div>
                                <div className="jg-meta">
                                    {formatDate(item.submittedAt)}
                                    {item.salesperson ? ` · ${item.salesperson}` : ''}
                                </div>
                                {editingId === item.id ? (
                                    <JgAdvisorCodeEditor
                                        item={item}
                                        onSaved={onSaved}
                                        onCancel={() => setEditingId(null)}
                                    />
                                ) : (
                                    <>
                                        <JgAdvisorList advisors={item.advisors} />
                                        <button className="jg-save" onClick={() => setEditingId(item.id)}>
                                            <Pencil size={14} /> Update Chamber Code
                                        </button>
                                    </>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default JgIncomingDrawer;
